import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  Eye,
  MoreVertical,
  Copy,
  Truck,
  ChevronDown,
  RefreshCw,
  Download,
  Phone,
  MessageCircle,
  FileText,
  User,
  CheckCircle,
  X,
  Calendar,
  Tag,
  Layout,
  CreditCard,
} from "lucide-react";
import ExpandableSearchBar from "../components/ExpandableSearchBar";
import { ordersApi } from "../services/api";
import { useTheme } from "../context/ThemeContext";
import ThemeSelect from "../components/ThemeSelect";
import Pagination from "../components/Pagination";
import ExcelExportButton from "../components/ExcelExportButton";
import DateRangeFilter from "../components/DateRangeFilter";
import { message, Modal } from "antd";
import { formatDate } from "../utils/helpers";
import { employeesApi, productsApi, orderCampaignApi } from "../services/api";
// Auth Helper Functions
const getAuthUser = () => {
  const userStr = localStorage.getItem("user");
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      console.error("Error parsing user:", e);
      return null;
    }
  }
  return null;
};

// Courier tracking URLs
const COURIER_TRACKING_URLS = {
  "E-kart": "https://ekartlogistics.com/track/",
  Ekart: "https://ekartlogistics.com/track/",
  Fship: "https://www.fship.in/track/",
  "F-ship": "https://www.fship.in/track/",
  Shadowfax: "https://tracker.shadowfax.in/#/",
  Delhivery: "https://www.delhivery.com/track/package/",
  DNST: "https://www.dtdc.in/tracking.asp?awb=",
  "Speed post":
    "https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx",
};

function DeliveredOrders() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === "#FFFFFF";
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourier, setSelectedCourier] = useState("All courier");
  const [ordersPerPage, setOrdersPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    agents: [],
    categories: [],
    campaigns: [],
  });
  const [activeFilters, setActiveFilters] = useState({
    date: "",
    customStartDate: "",
    customEndDate: "",
    agent: "",
    category: "",
    paymentMode: "",
    campaign: "",
    courier: "",
    cPartner: "",
    teamLead: "",
  });

  const [tempFilters, setTempFilters] = useState({
    date: "",
    customStartDate: "",
    customEndDate: "",
    agent: "",
    category: "",
    paymentMode: "",
    campaign: "",
    courier: "",
    cPartner: "",
    teamLead: "",
  });

  const courierOptions = [
    "All courier",
    "DNST",
    "E-kart",
    "F-ship",
    "Shadowfax",
    "Delhivery",
    "Speed post",
  ];
  const ordersPerPageOptions = [10, 20, 50, 100];

  const fetchFilterOptions = async () => {
    try {
      const [employeesRes, categoriesRes, campaignsRes] =
        await Promise.allSettled([
          employeesApi.getAllEmployees(),
          productsApi.getAllCategories(),
          orderCampaignApi.getAllCampaigns(),
        ]);

      const empRaw =
        employeesRes.status === "fulfilled" ? employeesRes.value : [];
      const agents = (Array.isArray(empRaw) ? empRaw : [])
        .map((e) => ({
          name: e.name || e.employeeName || e.fullName,
          empId: e.id,
        }))
        .filter((e) => e.name);

      const catRaw =
        categoriesRes.status === "fulfilled" ? categoriesRes.value : [];
      const categories = [
        ...new Set(
          (Array.isArray(catRaw) ? catRaw : [])
            .map((c) =>
              typeof c === "string" ? c : c?.categoryName || c?.name,
            )
            .filter(Boolean),
        ),
      ].sort();

      const campRaw =
        campaignsRes.status === "fulfilled" ? campaignsRes.value : [];
      const campaigns = [
        ...new Set(
          (Array.isArray(campRaw) ? campRaw : [])
            .map((c) => c?.campaignName || c?.name)
            .filter(Boolean),
        ),
      ].sort();

      setFilterOptions({ agents, categories, campaigns, actionBys: agents });
    } catch (err) {
      console.error("fetchFilterOptions error:", err);
    }
  };
  // Load user from localStorage
  useEffect(() => {
    const user = getAuthUser();
    setCurrentUser(user);
    setUserLoaded(true);
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (userLoaded) {
      setCurrentPage(1);
      fetchDeliveredOrders(1);
    }
  }, [selectedCourier, activeFilters, userLoaded]);

  const fetchDeliveredOrders = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = {
        page: page - 1,
        size: ordersPerPage,
      };

      // Courier filter (top bar se)
      if (selectedCourier !== "All courier") {
        params.courier = selectedCourier;
      }

      // Non-admin filter
      if (currentUser && currentUser.role !== "ADMIN") {
        params.employeeId = currentUser.id;
      }

      // Active filters
      if (activeFilters.agent) {
        params.employeeId = activeFilters.agent;
      }
      if (activeFilters.category) params.category = activeFilters.category;
      if (activeFilters.paymentMode)
        params.paymentMode = activeFilters.paymentMode;
      if (activeFilters.campaign) params.campaign = activeFilters.campaign;

      // Courier filter modal se
      if (activeFilters.courier && activeFilters.courier !== "All courier") {
        params.courier = activeFilters.courier;
      }

      // Team lead filter
      if (activeFilters.teamLead) {
        params.teamLeadId = activeFilters.teamLead;
      }

      // Date filter
      if (activeFilters.date && activeFilters.date !== "all_time") {
        const now = new Date();
        let startDate, endDate;
        const fmt = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        switch (activeFilters.date) {
          case "today":
            startDate = fmt(now);
            endDate = fmt(now);
            break;
          case "yesterday": {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            startDate = fmt(y);
            endDate = fmt(y);
            break;
          }
          case "this_week": {
            const day = now.getDay();
            const diff = now.getDate() - day + (day === 0 ? -6 : 1);
            startDate = fmt(new Date(now.getFullYear(), now.getMonth(), diff));
            endDate = fmt(now);
            break;
          }
          case "last_week": {
            const lw = new Date(now);
            lw.setDate(lw.getDate() - 7);
            startDate = fmt(lw);
            endDate = fmt(now);
            break;
          }
          case "this_month":
            startDate = fmt(new Date(now.getFullYear(), now.getMonth(), 1));
            endDate = fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0));
            break;
          case "last_month":
            startDate = fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1));
            endDate = fmt(new Date(now.getFullYear(), now.getMonth(), 0));
            break;
          case "this_year":
            startDate = fmt(new Date(now.getFullYear(), 0, 1));
            endDate = fmt(new Date(now.getFullYear(), 11, 31));
            break;
          case "custom":
            if (activeFilters.customStartDate)
              params.startDate = activeFilters.customStartDate;
            if (activeFilters.customEndDate)
              params.endDate = activeFilters.customEndDate;
            break;
          default:
            break;
        }

        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      }
      console.log("params", params);
      const response = await ordersApi.getDeliveredOrders(params);

      let ordersData = [];
      let total = 0;

      // ✅ API response: { success, total, orders: [...] }
      if (response?.orders) {
        ordersData = response.orders;
        total = response.total || response.orders.length;
      } else if (response?.data?.orders) {
        ordersData = response.data.orders;
        total = response.data.total || response.data.orders.length;
      } else if (response?.data) {
        ordersData = Array.isArray(response.data) ? response.data : [];
        total = response.total || ordersData.length;
      } else if (response?.content) {
        ordersData = response.content;
        total = response.totalElements || response.content.length;
      } else if (Array.isArray(response)) {
        ordersData = response;
        total = response.length;
      }

      const mappedOrders = ordersData.map((order, index) => ({
        id: order.id || index + 1,
        orderId: order.orderId || `OD${order.id}`,
        orderDate: order.orderDate || order.createdAt || "-",
        deliveredDate: order.deliveryDate || order.deliveredOn || "-",
        rawDate:
          order.deliveryDate ||
          order.deliveredOn ||
          order.orderDate ||
          order.createdAt ||
          null,
        customerName: order.customerName || "-",
        phone: order.contactNumber || "-",
        alternateNumber: order.alternateNumber || null,
        whatsappSent: order.whatsappSent || false,
        product:
          order.orderItems?.length > 0
            ? order.orderItems.map((item) => item.productName).join(", ")
            : order.productName || "-",
        category: order.orderItems?.[0]?.category || order.category || "-",
        amount: order.grandTotal || order.restAmount || order.netAmount || 0,
        paymentMode: order.paymentMode || "COD",
        orderCampaign: order.leadSource || order.orderCampaign || "-",
        cPartner: order.courierName || order.cPartner || "-",
        courier: order.courierName || "-",
        awbNumber: order.awbNumber || order.trackingNumber || "-",
        status: order.status || "DELIVERED",
        orderBy: order.createdByName || order.assignedBy || "-",
        orderByEmp: order.createdById || "-",
        teamLead: order.teamName || order.assignedTo || "-",
        teamLeadEmp: order.assignedToId || "-",
        leadId: order.leadId,
        leadNumber: order.leadNumber,
        remarks: order.remarks || "",
      }));

      setOrders(mappedOrders);
      setTotalOrders(total);
    } catch (error) {
      console.error("Error fetching delivered orders:", error);
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (order) => {
    navigate(`/orders/${order.id}/details`, {
      state: { activeTab: "DELIVERED" },
    });
  };

  const handleOrderHistory = (order) => {
    navigate(`/orders/${order.id}/history`);
  };

  const handleEditOrder = (order) => {
    navigate(`/orders/${order.id}/edit`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleRefresh = () => {
    fetchDeliveredOrders();
  };

  const handleExport = () => {
    if (orders.length === 0) {
      message.warning({
        content: "No data available to export",
        style: { marginTop: "10vh" },
      });
      return;
    }
    try {
      const headers = [
        "Order ID",
        "Order Date",
        "Delivered Date",
        "Customer Name",
        "Phone",
        "Product",
        "Category",
        "Amount",
        "Pay Mode",
        "Courier",
        "AWB Number",
        "Order By",
      ];
      const rows = orders.map((order) => [
        order.orderId,
        `"${formatDate(order.orderDate)}"`,
        `"${formatDate(order.deliveredDate)}"`,
        `"${order.customerName}"`,
        order.phone,
        `"${order.product}"`,
        order.category,
        order.amount,
        order.paymentMode,
        order.courier,
        order.awbNumber,
        `"${order.orderBy}"`,
      ]);
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Delivered_Orders_${new Date().toLocaleDateString()}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success({
        content: "Report downloaded successfully!",
        style: { marginTop: "10vh" },
      });
    } catch (error) {
      message.error("Failed to export report");
      console.error(error);
    }
  };

  const uniqueCategories = [
    ...new Set(orders.map((o) => o.category).filter(Boolean)),
  ];
  const uniqueAgents = [
    ...new Set(orders.map((o) => o.orderBy).filter(Boolean)),
  ];
  const uniqueCampaigns = [
    ...new Set(orders.map((o) => o.orderCampaign).filter(Boolean)),
  ];
  const uniqueCouriers = [
    ...new Set(orders.map((o) => o.courier).filter(Boolean)),
  ];
  const uniqueCPartners = [
    ...new Set(orders.map((o) => o.cPartner).filter(Boolean)),
  ];
  const uniqueTeamLeads = [
    ...new Set(orders.map((o) => o.teamLead).filter(Boolean)),
  ];

  // Frontend search filter only (API already handles other filters)
  const filteredOrders = orders.filter((order) => {
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      return (
        order.customerName?.toLowerCase().includes(query) ||
        order.orderId?.toLowerCase().includes(query) ||
        order.phone?.toLowerCase().includes(query) ||
        order.awbNumber?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const showingFrom =
    filteredOrders.length > 0 ? (currentPage - 1) * ordersPerPage + 1 : 0;
  const showingTo = Math.min(
    currentPage * ordersPerPage,
    filteredOrders.length,
  );

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? "bg-[#0f172a]" : "bg-gray-50"}`}
    >
      <div className="flex flex-col gap-6">
        {/* Filter Modal */}
        {isFilterModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] p-4 overflow-y-auto">
            <div className="flex items-center justify-center min-h-full">
              <div className="bg-white rounded-xl w-full max-w-2xl mx-auto flex flex-col shadow-xl my-8 relative">
                {/* Header */}
                <div
                  className="flex items-center justify-between px-6 py-4 border-b border-gray-200"
                  style={{ background: currentTheme.color }}
                >
                  <h2 className="text-lg font-bold text-white">
                    Filter Orders
                  </h2>
                  <button
                    onClick={() => setIsFilterModalOpen(false)}
                    className="p-1 hover:bg-white/20 rounded transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Filter Content */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 1. Date Range */}
                    <div className="col-span-1">
                      <DateRangeFilter
                        label="Delivered Date"
                        preset={tempFilters.date}
                        startDate={tempFilters.customStartDate}
                        endDate={tempFilters.customEndDate}
                        onChange={({ preset, startDate, endDate }) =>
                          setTempFilters({
                            ...tempFilters,
                            date: preset,
                            customStartDate: startDate,
                            customEndDate: endDate,
                          })
                        }
                      />
                    </div>

                    {/* 2. Agents */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agents
                      </label>
                      <ThemeSelect
                        value={tempFilters.agentName || ""}
                        onChange={(val) => {
                          const selectedAgent = filterOptions.agents.find(
                            (a) => a.name === val,
                          );
                          setTempFilters({
                            ...tempFilters,
                            agent: selectedAgent?.empId || "", // ✅ ID pass होगी
                            agentName: val, // optional: display के लिए
                          });
                        }}
                        options={[
                          { label: "All Agents", value: "" },
                          ...filterOptions.agents.map((a) => ({
                            label: a.name,
                            value: a.name,
                          })),
                        ]}
                      />
                    </div>

                    {/* 3. Category */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <ThemeSelect
                        value={tempFilters.category}
                        onChange={(val) =>
                          setTempFilters({ ...tempFilters, category: val })
                        }
                        options={[
                          { label: "All Categories", value: "" },
                          ...filterOptions.categories.map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                        icon={Tag}
                        className="w-full"
                      />
                    </div>

                    {/* 4. Payment Mode */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Mode
                      </label>
                      <ThemeSelect
                        value={tempFilters.paymentMode}
                        onChange={(val) =>
                          setTempFilters({ ...tempFilters, paymentMode: val })
                        }
                        options={[
                          { label: "All Modes", value: "" },
                          { label: "COD", value: "COD" },
                          { label: "Online", value: "ONLINE" },
                          { label: "UPI", value: "UPI" },
                          { label: "Card", value: "CARD" },
                        ]}
                        icon={CreditCard}
                        className="w-full"
                        placeholder="All Modes"
                      />
                    </div>

                    {/* 5. Campaign */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Campaign
                      </label>
                      <ThemeSelect
                        value={tempFilters.campaign}
                        onChange={(val) =>
                          setTempFilters({ ...tempFilters, campaign: val })
                        }
                        options={[
                          { label: "All Campaigns", value: "" },
                          ...filterOptions.campaigns.map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                        icon={Layout}
                        className="w-full"
                      />
                    </div>

                    {/* 6. Courier */}
                    <div className="col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Courier
                      </label>
                      <ThemeSelect
                        value={tempFilters.courier}
                        onChange={(val) =>
                          setTempFilters({ ...tempFilters, courier: val })
                        }
                        options={[
                          { label: "All Couriers", value: "" },
                          ...uniqueCouriers.map((c) => ({
                            label: c,
                            value: c,
                          })),
                        ]}
                        icon={Truck}
                        className="w-full"
                        placeholder="All Couriers"
                      />
                    </div>

                    {/* 7. Courier Partner */}
                    {/* <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">C. Partner</label>
                    <ThemeSelect
                      value={tempFilters.cPartner}
                      onChange={(val) => setTempFilters({ ...tempFilters, cPartner: val })}
                      options={[{ label: 'All Partners', value: '' }, ...uniqueCPartners.map(c => ({ label: c, value: c }))]}
                      icon={Truck}
                      className="w-full"
                      placeholder="All Partners"
                    />
                  </div> */}

                    {/* 8. Team Lead */}
                    {/* <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Team Lead</label>
                    <ThemeSelect
                      value={tempFilters.teamLead}
                      onChange={(val) => setTempFilters({ ...tempFilters, teamLead: val })}
                      options={[{ label: 'All Team Leads', value: '' }, ...uniqueTeamLeads.map(t => ({ label: t, value: t }))]}
                      icon={User}
                      className="w-full"
                      placeholder="All Team Leads"
                    />
                  </div> */}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50 rounded-b-xl">
                  <button
                    onClick={() => {
                      const emptyFilters = {
                        date: "",
                        customStartDate: "",
                        customEndDate: "",
                        agent: "",
                        category: "",
                        paymentMode: "",
                        campaign: "",
                        courier: "",
                        cPartner: "",
                        teamLead: "",
                      };
                      setTempFilters({ ...emptyFilters });
                      setActiveFilters({ ...emptyFilters });
                      setCurrentPage(1);
                      setIsFilterModalOpen(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => {
                      setActiveFilters({ ...tempFilters }); // ✅ naya object reference
                      setCurrentPage(1);
                      setIsFilterModalOpen(false);
                    }}
                    className="px-6 py-2 text-sm font-medium text-white shadow-sm hover:shadow rounded-lg transition-colors"
                    style={{ backgroundColor: currentTheme.color }}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <h1
              className={`text-2xl desktop:text-3xl font-bold ${isDark ? "text-white" : "text-gray-800"}`}
            >
              Delivered
            </h1>
            <span className="text-gray-500 text-lg desktop:text-2xl">
              ({totalOrders.toLocaleString()})
            </span>
          </div>
          <div className="flex items-center space-x-3">
            {currentUser && (
              <div
                className={`flex items-center space-x-2 border rounded-lg px-3 py-2 desktop:px-4 desktop:py-3 ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}
              >
                <div
                  className={`w-8 h-8 desktop:w-10 desktop:h-10 rounded-full flex items-center justify-center ${isDark ? "bg-white/5" : "bg-teal-100"}`}
                >
                  <User
                    className={`w-4 h-4 desktop:w-6 desktop:h-6 ${isDark ? "text-gray-400" : "text-teal-600"}`}
                  />
                </div>
                <div className="text-sm desktop:text-base">
                  <p
                    className={`font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}
                  >
                    {currentUser.name}
                  </p>
                  <p
                    className={`text-xs desktop:text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {currentUser.role}
                  </p>
                </div>
              </div>
            )}
            <button
              onClick={handleRefresh}
              className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? "bg-[#111827] border-white/5 hover:bg-white/5" : "bg-white border-gray-200 hover:bg-gray-50"}`}
            >
              <RefreshCw
                className={`w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className={`p-2.5 desktop:p-3.5 border rounded-lg transition-all ${isDark ? "bg-[#111827] border-white/5 hover:bg-white/5" : "bg-white border-gray-200 hover:bg-gray-50"}`}
            >
              <Filter className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-500" />
            </button>
            <ExcelExportButton onClick={handleExport} isDownloading={loading} />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4 mb-6 flex-wrap gap-y-3">
          <ExpandableSearchBar
            searchTerm={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            placeholder="Search by order id, name, phone or AWB"
          />
          <ThemeSelect
            value={`${ordersPerPage} Orders`}
            onChange={(val) => {
              const newSize = parseInt(val);
              setOrdersPerPage(newSize);
              setCurrentPage(1);
              fetchDeliveredOrders(1);
            }}
            options={ordersPerPageOptions.map((opt) => `${opt} Orders`)}
            className="w-44 desktop:w-60"
          />
          <ThemeSelect
            value={selectedCourier}
            onChange={(val) => {
              setSelectedCourier(val);
              setCurrentPage(1);
            }}
            options={courierOptions}
            icon={Truck}
            className="w-48 desktop:w-64"
          />
        </div>

        {/* Table */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "bg-[#111827] border-white/5" : "bg-white border-gray-200"}`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1800px]">
              <thead
                className="text-white"
                style={{ background: currentTheme.color }}
              >
                <tr>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Order Date
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Delivered Date
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Pay Mode
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Order Campaign
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    C.Partner
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Courier
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    AWB No.
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Order By
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Team Lead
                  </th>
                  <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center text-xs desktop:text-base font-semibold uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}
              >
                {loading ? (
                  <tr>
                    <td colSpan={16} className="px-4 py-12 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-gray-500">
                          Loading delivered orders...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : filteredOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={16}
                      className="px-4 py-12 text-center text-gray-500"
                    >
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p>No delivered orders found</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order, idx) => (
                    <tr
                      key={order.id}
                      className={`transition-colors ${isDark ? (idx % 2 === 0 ? "bg-[#111827]" : "bg-white/[0.02]") + " hover:bg-white/5" : (idx % 2 === 0 ? "bg-white" : "bg-gray-50/50") + " hover:bg-gray-50"}`}
                    >
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}
                        >
                          {order.orderId}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className="text-sm desktop:text-base text-gray-600 whitespace-nowrap">
                          {formatDate(order.orderDate)}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span className="text-sm desktop:text-base text-gray-600 whitespace-nowrap">
                          {formatDate(order.deliveredDate)}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span
                            className={`text-sm desktop:text-base font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}
                          >
                            {order.customerName}
                          </span>
                          <div className="flex items-center space-x-1.5 mt-0.5">
                            <Phone className="w-3.5 h-3.5 desktop:w-4 desktop:h-4 text-gray-500" />
                            <span
                              className={`text-xs desktop:text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {order.phone}
                            </span>
                            {order.whatsappSent && (
                              <div className="w-4 h-4 desktop:w-5 desktop:h-5 bg-green-500 rounded-full flex items-center justify-center">
                                <MessageCircle className="w-2.5 h-2.5 desktop:w-3 desktop:h-3 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? "text-gray-300" : "text-gray-700"} truncate block max-w-[150px]`}
                          title={order.product}
                        >
                          {order.product}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {order.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className="text-sm desktop:text-base font-semibold"
                          style={{ color: currentTheme.color }}
                        >
                          ₹ {order.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {order.paymentMode === "ONLINE"
                            ? "Online"
                            : order.paymentMode === "CASH" ||
                                order.paymentMode === "COD"
                              ? "COD"
                              : order.paymentMode || "COD"}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {order.orderCampaign}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {order.cPartner}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`text-sm desktop:text-base ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {order.courier}
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div
                          className={`flex items-center space-x-2 w-fit px-3 py-1.5 rounded-lg transition-all ${isDark ? "bg-white/5 border border-white/5 hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200"}`}
                        >
                          <span
                            className={`text-sm desktop:text-base font-mono font-bold ${isDark ? "text-yellow-400" : "text-teal-700"}`}
                          >
                            {order.awbNumber}
                          </span>
                          <button
                            onClick={() => copyToClipboard(order.awbNumber)}
                            className={`p-1 rounded transition-colors ${isDark ? "hover:bg-white/10" : "hover:bg-white"}`}
                            title="Copy AWB"
                          >
                            <Copy
                              className={`w-3.5 h-3.5 desktop:w-4 desktop:h-4 ${isDark ? "text-gray-400" : "text-gray-400"}`}
                            />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <span
                          className={`inline-flex items-center space-x-1 px-3 py-1.5 desktop:px-4 desktop:py-2 text-xs desktop:text-sm font-medium rounded-full ${isDark ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-green-50 text-green-700 border border-green-200"}`}
                        >
                          <span>Delivered</span>
                          <CheckCircle className="w-3.5 h-3.5 desktop:w-4 desktop:h-4" />
                        </span>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span
                            className={`text-sm desktop:text-base font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}
                          >
                            {order.orderBy}
                          </span>
                          <span
                            className={`text-xs desktop:text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            Emp: {order.orderByEmp}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span
                            className={`text-sm desktop:text-base font-medium ${isDark ? "text-gray-200" : "text-gray-800"}`}
                          >
                            {order.teamLead}
                          </span>
                          <span
                            className={`text-xs desktop:text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            Emp: {order.teamLeadEmp}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-center relative">
                        <button
                          onClick={() =>
                            setShowActionMenu(
                              showActionMenu === order.id ? null : order.id,
                            )
                          }
                          className="p-2 desktop:p-3 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-600" />
                        </button>
                        {showActionMenu === order.id && (
                          <div className="absolute right-4 top-12 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                            <button
                              onClick={() => {
                                handleViewDetails(order);
                                setShowActionMenu(null);
                              }}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Eye className="w-4 h-4" />
                              <span>Order Details</span>
                            </button>
                            <button
                              onClick={() => {
                                handleOrderHistory(order);
                                setShowActionMenu(null);
                              }}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Order History</span>
                            </button>
                            <button
                              onClick={() => {
                                handleEditOrder(order);
                                setShowActionMenu(null);
                              }}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                              <span>Edit Order</span>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 px-6 py-4">
            <Pagination
              currentPage={currentPage}
              totalItems={totalOrders}
              itemsPerPage={ordersPerPage}
              onPageChange={(page) => {
                setCurrentPage(page);
                fetchDeliveredOrders(page);
              }}
            />
          </div>
        </div>

        {/* Click outside to close action menu */}
        {showActionMenu && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowActionMenu(null)}
          />
        )}
      </div>
    </div>
  );
}

export default DeliveredOrders;
